import { ReactNode } from 'react'
import { BondRating } from '@prisma/client'
import { invert } from 'remeda'
import { Money } from 'lib/money'
import { DiscountSelector } from 'containers/calculator/components/DiscountSelector'
import { Slider } from 'containers/calculator/components/Slider'
import { useCalculatorInput } from 'containers/calculator/hooks/useCalculatorInput'
import styles from 'containers/calculator/styles/components/calculatorInput.module.css'
import DashboardIcon from 'public/img/dashboard.svg'
import InvoicesIcon from 'public/img/invoices.svg'
import PaybackIcon from 'public/img/payback.svg'

const MAXIMUM_INVOICE_AMOUNT = 100000
const MINIMUM_INVOICE_AMOUNT = 1000
const MAXIMUM_TERM = 6

const Label = ({ icon: Icon, children }: { icon: any; children: ReactNode }) => {
    return (
        <div className={styles['label']}>
            <Icon />
            <p>{children}</p>
        </div>
    )
}

const InvoiceAmountSliderRow = () => {
    const { values, setValues } = useCalculatorInput()

    const handleChange = (val: string) => {
        setValues({ ...values, amount: parseInt(val) })
    }

    return (
        <>
            <div className={styles['hide-mobile']}>
                <Label icon={InvoicesIcon}>Invoice amount</Label>
            </div>
            <div>
                <div className={styles['hide-desktop']}>
                    <Label icon={InvoicesIcon}>Invoice amount</Label>
                </div>
                <Slider
                    step={1000}
                    max={MAXIMUM_INVOICE_AMOUNT}
                    min={MINIMUM_INVOICE_AMOUNT}
                    onChange={handleChange}
                    value={values.amount}
                />
            </div>
            <div>
                <p>
                    {Money.format(Money.normalize(values.amount), {
                        notation: 'compact',
                        minimumFractionDigits: 0,
                    })}
                </p>
            </div>
        </>
    )
}

const TermSliderRow = () => {
    const { values, setValues } = useCalculatorInput()

    const handleChange = (val: string) => {
        setValues({ ...values, term: parseInt(val) })
    }

    return (
        <>
            <div className={styles['hide-mobile']}>
                <Label icon={() => <PaybackIcon className={styles['payback-icon']} />}>
                    Months to pay back
                </Label>
            </div>
            <div>
                <div className={styles['hide-desktop']}>
                    <Label
                        icon={() => <PaybackIcon className={styles['payback-icon']} />}
                    >
                        Months to pay back
                    </Label>
                </div>
                <Slider
                    step={1}
                    max={MAXIMUM_TERM}
                    min={1}
                    onChange={handleChange}
                    value={values.term}
                />
            </div>
            <div>
                <p>{values.term}</p>
            </div>
        </>
    )
}

const bondRatingsToLabel = {
    [BondRating.CCC_PLUS]: 'Poor',
    [BondRating.BBB_PLUS]: 'Average',
    [BondRating.AA]: 'Good',
}

const bondRatingToRatingNumber = {
    [BondRating.CCC_PLUS]: 0,
    [BondRating.BBB_PLUS]: 1,
    [BondRating.AA]: 2,
}

const bondRatingNumberToBondRating = invert(bondRatingToRatingNumber)

const BondRatingSliderRow = () => {
    const { values, setValues } = useCalculatorInput()

    const handleChange = (val: string) => {
        setValues({ ...values, rating: bondRatingNumberToBondRating[parseInt(val)] })
    }

    return (
        <>
            <div className={styles['hide-mobile']}>
                <Label icon={DashboardIcon}>Credit rating</Label>
            </div>
            <div>
                <div className={styles['hide-desktop']}>
                    <Label icon={DashboardIcon}>Credit rating</Label>
                </div>
                <Slider
                    step={1}
                    max={2}
                    min={0}
                    onChange={handleChange}
                    value={bondRatingToRatingNumber[values.rating]}
                />
            </div>
            <div>
                <p>{bondRatingsToLabel[values.rating]}</p>
            </div>
        </>
    )
}
export const CalculatorInput = () => {
    return (
        <div className={styles['calculator-input']}>
            <div className={styles['grid']}>
                <InvoiceAmountSliderRow />
                <TermSliderRow />
                <BondRatingSliderRow />
            </div>
            <DiscountSelector />
        </div>
    )
}
